/**
 * PersonNode - React SVG component for a person in concentric layout
 *
 * Renders a circle with initials, name label, and optional badge.
 * Supports click, hover, keyboard focus, and screen reader labels.
 */

import React from 'react';
import { ZONE_CONFIG, EGO_CONFIG, CONCENTRIC_LAYOUT, BADGE_TYPES } from '../../constants/dashboardConfig';
import { getInitials } from '../../utils/networkUtils';

// eslint-disable-next-line max-lines-per-function
function PersonNode({
  node,
  isSelected,
  isHighlighted,
  onSelect,
}) {
  const { x, y, label, zone, isEgo, badge, nameResolved } = node;
  const size = isEgo ? CONCENTRIC_LAYOUT.nodeSize.ego : CONCENTRIC_LAYOUT.nodeSize.default;
  const radius = size / 2;
  const fontSize = isEgo ? CONCENTRIC_LAYOUT.fontSize.ego : CONCENTRIC_LAYOUT.fontSize.default;
  const initials = getInitials(label);

  // Gradient colors
  const zoneConf = ZONE_CONFIG[zone];
  const gradientColors = isEgo ? EGO_CONFIG.gradient : (zoneConf?.gradient || ['#767676', '#555']);
  const gradientId = `node-gradient-${node.id}`;

  // Selection / highlight styling
  const glowRadius = radius + 4;
  const showGlow = isSelected || (isEgo && !isSelected);

  const dimmed = !isSelected && !isHighlighted && !isEgo;

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={`${label}, ${zone || 'ego'} zone, ${node.degree || 0} connections`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(node);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onSelect(node);
        }
      }}
      style={{
        cursor: 'pointer',
        outline: 'none',
        opacity: dimmed ? 0.5 : 1,
        transition: 'opacity 0.3s',
      }}
    >
      {/* Gradient definition */}
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={gradientColors[0]} />
          <stop offset="100%" stopColor={gradientColors[1]} />
        </linearGradient>
      </defs>

      {/* Selection / ego glow ring */}
      {showGlow && (
        <circle
          cx={x}
          cy={y}
          r={glowRadius}
          fill="none"
          stroke={isSelected ? '#23A38F' : 'rgba(35, 163, 143, 0.3)'}
          strokeWidth={isSelected ? 3 : isEgo ? 4 : 2}
          style={{ transition: 'all 0.3s' }}
        />
      )}

      {/* Focus indicator */}
      <circle
        cx={x}
        cy={y}
        r={glowRadius + 2}
        fill="none"
        stroke="transparent"
        strokeWidth={2}
        className="focus-ring"
        style={{
          transition: 'stroke 0.2s',
        }}
      />

      {/* Main circle */}
      <circle
        cx={x}
        cy={y}
        r={radius}
        fill={`url(#${gradientId})`}
        style={{
          filter: `drop-shadow(0 2px 4px rgba(0,0,0,0.2))`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      {/* Initials text */}
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontSize={fontSize}
        fontWeight={700}
        fontFamily="DM Sans, sans-serif"
        style={{ pointerEvents: 'none' }}
      >
        {initials}
      </text>

      {/* Name label below */}
      <text
        x={x}
        y={y + radius + 14}
        textAnchor="middle"
        dominantBaseline="hanging"
        fill="rgba(0,0,0,0.87)"
        fontSize={CONCENTRIC_LAYOUT.fontSize.name}
        fontWeight={600}
        fontFamily="DM Sans, sans-serif"
        style={{ pointerEvents: 'none', textShadow: '0 1px 2px rgba(255,255,255,0.8)' }}
      >
        {label}
      </text>

      {/* Name resolution indicator */}
      {!nameResolved && !isEgo && (
        <text
          x={x}
          y={y + radius + 26}
          textAnchor="middle"
          dominantBaseline="hanging"
          fill="rgba(0,0,0,0.38)"
          fontSize={8}
          fontFamily="DM Sans, sans-serif"
          style={{ pointerEvents: 'none' }}
        >
          (hash)
        </text>
      )}

      {/* Badge indicator */}
      {badge && BADGE_TYPES[badge] && (
        <circle
          cx={x + radius * 0.7}
          cy={y - radius * 0.7}
          r={10}
          fill={BADGE_TYPES[badge].color}
          stroke="white"
          strokeWidth={2}
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
        />
      )}

      {/* CSS for focus-visible */}
      <style>{`
        g:focus-visible .focus-ring {
          stroke: #1976d2;
          stroke-width: 2;
        }
      `}</style>
    </g>
  );
}

export default PersonNode;
